import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  SELFEVAL_SECTIONS, CALM_OPTIONS, FAVOURITE_FIELDS, RATING_HINT,
  sectionAverage, ratingColour,
} from "@/lib/selfEval";
import { SUBJECT_META } from "@/lib/scale";
import { ArrowLeft, Printer, Loader2, Sprout, Save, Sparkles, Target, Star, Check } from "lucide-react";

const T = { green: "#B3E5A1", greenDeep: "#7FB069", slate: "#44546A", grey: "#F2F2F2" };

function RatingRow({ label, value, onChange, testId }) {
  return (
    <div className="flex items-center gap-3 py-2 border-b border-slate-50 last:border-0">
      <span className="flex-1 text-sm text-slate-700">{label}</span>
      <div className="flex gap-1" data-testid={testId}>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => {
          const on = value === n;
          return (
            <button key={n} type="button" data-testid={`${testId}-${n}`} onClick={() => onChange(on ? 0 : n)}
              className={`h-7 w-7 rounded-full text-xs font-bold transition border ${on ? "text-white border-transparent scale-110" : "text-slate-400 border-slate-200 hover:border-slate-300"}`}
              style={on ? { backgroundColor: ratingColour(n) } : {}}>
              {n}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, placeholder, rows = 2, testId }) {
  return (
    <label className="block mt-3">
      <span className="text-xs font-bold uppercase tracking-wide text-slate-400">{label}</span>
      <textarea data-testid={testId} rows={rows} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-1 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 outline-none focus:border-[#7FB069] focus:bg-white resize-none transition" />
    </label>
  );
}

export default function SelfEval() {
  const { studentId, evalId } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [data, setData] = useState({
    title: "Baseline", ratings: {}, strengths: ["", "", ""], proud_of: "", improve: "",
    calm_helps: [], calm_other: "", favourites: {}, anything_else: "", goal: "", goal_measure: "",
    teacher_comments: "", summary: "",
  });
  const [id, setId] = useState(evalId || null);
  const [saving, setSaving] = useState(false);
  const [aiSum, setAiSum] = useState(false);
  const [aiTarget, setAiTarget] = useState(false);

  const load = useCallback(() => {
    api.get(`/students/${studentId}`).then((r) => setStudent(r.data)).catch(() => navigate("/"));
    if (evalId) {
      api.get(`/self-evaluations/${evalId}`).then((r) => {
        const d = r.data;
        setData({
          title: d.title || "Baseline", ratings: d.ratings || {},
          strengths: [(d.strengths || [])[0] || "", (d.strengths || [])[1] || "", (d.strengths || [])[2] || ""],
          proud_of: d.proud_of || "", improve: d.improve || "", calm_helps: d.calm_helps || [],
          calm_other: d.calm_other || "", favourites: d.favourites || {}, anything_else: d.anything_else || "",
          goal: d.goal || "", goal_measure: d.goal_measure || "", teacher_comments: d.teacher_comments || "",
          summary: d.summary || "",
        });
      }).catch(() => navigate(`/student/${studentId}`));
    }
  }, [studentId, evalId, navigate]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setRating = (item, v) => setData((d) => ({ ...d, ratings: { ...d.ratings, [item]: v } }));
  const toggleCalm = (k) => setData((d) => ({
    ...d, calm_helps: d.calm_helps.includes(k) ? d.calm_helps.filter((x) => x !== k) : [...d.calm_helps, k],
  }));

  const payload = () => ({ ...data, student_id: studentId, strengths: data.strengths.filter((s) => s.trim()) });

  const save = async () => {
    setSaving(true);
    try {
      if (id) {
        await api.put(`/self-evaluations/${id}`, payload());
      } else {
        const { data: created } = await api.post("/self-evaluations", payload());
        setId(created.id);
        navigate(`/student/${studentId}/self-eval/${created.id}`, { replace: true });
      }
      toast.success("Self-evaluation saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const ensureSaved = async () => {
    if (id) { await api.put(`/self-evaluations/${id}`, payload()); return id; }
    const { data: created } = await api.post("/self-evaluations", payload());
    setId(created.id);
    navigate(`/student/${studentId}/self-eval/${created.id}`, { replace: true });
    return created.id;
  };

  const summarise = async () => {
    setAiSum(true);
    try {
      const eid = await ensureSaved();
      const { data: res } = await api.post(`/self-evaluations/${eid}/summarise`);
      set("summary", res.summary);
      toast.success("Summary ready — review and edit");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAiSum(false);
    }
  };

  const suggestTarget = async () => {
    setAiTarget(true);
    try {
      const eid = await ensureSaved();
      await api.post(`/self-evaluations/${eid}/suggest-target`);
      toast.success("Social & Emotional priority target added to this pupil's profile.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAiTarget(false);
    }
  };

  if (!student)
    return <div className="min-h-screen flex justify-center items-center text-slate-300"><Loader2 className="animate-spin" /></div>;

  const seAssess = (student.assessments || []).find((a) => a.subject === "social_emotional");
  const seScores = seAssess?.scores || {};

  return (
    <div className="min-h-screen bg-[#F9F9F7] py-8 px-4">
      {/* Toolbar */}
      <div className="max-w-3xl mx-auto flex items-center justify-between gap-3 mb-6 no-print flex-wrap">
        <button data-testid="selfeval-back-btn" onClick={() => navigate(`/student/${studentId}`)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition">
          <ArrowLeft size={16} /> Back to profile
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="selfeval-suggest-target-btn" onClick={suggestTarget} disabled={aiTarget}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold font-['Outfit'] bg-white border border-slate-200 text-slate-700 hover:border-[#9A7AA0] transition disabled:opacity-50">
            {aiTarget ? <Loader2 size={16} className="animate-spin" /> : <Target size={16} />} Suggest SE target
          </button>
          <button data-testid="selfeval-summarise-btn" onClick={summarise} disabled={aiSum}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold font-['Outfit'] text-white transition disabled:opacity-50"
            style={{ background: `linear-gradient(135deg,${T.greenDeep},#5d8a5a)` }}>
            {aiSum ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Summarise for me
          </button>
          <button data-testid="selfeval-save-btn" onClick={save} disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold font-['Outfit'] bg-white border border-slate-200 text-slate-700 hover:border-[#7FB069] transition disabled:opacity-50">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
          </button>
          <button data-testid="selfeval-print-btn" onClick={() => window.print()}
            className="text-white rounded-full px-5 py-2 font-semibold font-['Outfit'] transition flex items-center gap-2" style={{ backgroundColor: T.slate }}>
            <Printer size={17} /> Print
          </button>
        </div>
      </div>

      {/* Sheet */}
      <div className="print-area max-w-3xl mx-auto bg-white rounded-2xl shadow-[0_4px_24px_rgba(42,59,71,0.06)] overflow-hidden">
        <div className="px-8 sm:px-10 pt-8 pb-5 flex items-center justify-between" style={{ borderBottom: `3px solid ${T.greenDeep}` }}>
          <div className="flex items-center gap-2.5">
            <span className="h-9 w-9 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: T.greenDeep }}><Star size={18} /></span>
            <span className="font-['Outfit'] font-bold text-lg" style={{ color: T.slate }}>My Social &amp; Emotional Profile</span>
          </div>
          <p className="text-sm text-slate-400">Get to Know Me</p>
        </div>

        <div className="px-8 sm:px-10 py-7">
          <h1 className="font-['Outfit'] text-3xl font-bold" style={{ color: T.slate }}>{student.name}</h1>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4 text-sm">
            <Info label="Class" value={`${student.class_level || ""}${student.class_name ? " · " + student.class_name : ""}`} />
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Title</span>
              <input data-testid="selfeval-title-input" value={data.title} onChange={(e) => set("title", e.target.value)}
                placeholder="e.g. Baseline – Sept"
                className="mt-0.5 w-full bg-transparent border-b border-slate-200 pb-1 outline-none font-semibold font-['Outfit'] text-slate-800 focus:border-[#7FB069]" />
            </label>
            <Info label="Date" value={new Date().toLocaleDateString("en-IE", { day: "numeric", month: "long", year: "numeric" })} />
          </div>

          <div className="mt-5 rounded-xl px-4 py-3 text-sm text-slate-600 flex flex-wrap gap-x-5 gap-y-1" style={{ backgroundColor: T.grey }}>
            <span className="font-bold text-slate-700">How to rate:</span>
            {Object.entries(RATING_HINT).map(([n, t]) => (
              <span key={n}><b style={{ color: ratingColour(+n) }}>{n}</b> = {t}</span>
            ))}
          </div>

          {/* Rating sections */}
          {SELFEVAL_SECTIONS.map((s) => {
            const avg = sectionAverage(data.ratings, s);
            const teacher = seScores[s.domain];
            const gap = avg != null && teacher ? Math.round(Math.abs(avg - teacher)) : null;
            return (
              <section key={s.key} className="mt-7" data-testid={`selfeval-section-${s.key}`}>
                <div className="flex items-center justify-between flex-wrap gap-2 border-b-2 pb-2" style={{ borderColor: SUBJECT_META.social_emotional.tint }}>
                  <h2 className="font-['Outfit'] text-lg font-bold text-slate-800">{s.emoji} {s.title}</h2>
                  <div className="flex items-center gap-2 text-xs">
                    {avg != null && <span className="rounded-full px-2.5 py-1 font-bold text-white" style={{ backgroundColor: ratingColour(Math.round(avg)) }} data-testid={`selfeval-avg-${s.key}`}>Self: {avg}</span>}
                    {teacher ? <span className="rounded-full px-2.5 py-1 font-bold bg-slate-100 text-slate-600" data-testid={`selfeval-teacher-${s.key}`}>Teacher: {teacher}</span> : null}
                    {gap != null && gap >= 3 && (
                      <span className="rounded-full px-2.5 py-1 font-bold bg-amber-50 text-amber-700" data-testid={`selfeval-gap-${s.key}`}>Gap {gap} — discuss</span>
                    )}
                  </div>
                </div>
                <div className="mt-2">
                  {s.items.map((it) => (
                    <RatingRow key={it.key} label={`I can ${it.label.charAt(0).toLowerCase()}${it.label.slice(1)}`}
                      value={data.ratings[it.key] || 0} onChange={(v) => setRating(it.key, v)} testId={`rating-${it.key}`} />
                  ))}
                </div>
              </section>
            );
          })}

          {/* All About Me */}
          <section className="mt-9" data-testid="selfeval-about">
            <h2 className="font-['Outfit'] text-xl font-bold border-b-2 pb-2" style={{ color: T.slate, borderColor: T.green }}>🌟 All About Me</h2>
            <div className="grid sm:grid-cols-3 gap-3 mt-3">
              {[0, 1, 2].map((i) => (
                <label key={i} className="block">
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Good at #{i + 1}</span>
                  <input data-testid={`selfeval-strength-${i}`} value={data.strengths[i]}
                    onChange={(e) => set("strengths", data.strengths.map((s, idx) => (idx === i ? e.target.value : s)))}
                    className="mt-1 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#7FB069] focus:bg-white" />
                </label>
              ))}
            </div>
            <TextField label="Something I am proud of" value={data.proud_of} onChange={(v) => set("proud_of", v)} testId="selfeval-proud" />
            <TextField label="Something I would like to get better at" value={data.improve} onChange={(v) => set("improve", v)} testId="selfeval-improve" />

            <div className="mt-4">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-400">When I feel upset, it helps when…</span>
              <div className="flex flex-wrap gap-2 mt-2" data-testid="selfeval-calm">
                {CALM_OPTIONS.map((o) => {
                  const on = data.calm_helps.includes(o.key);
                  return (
                    <button key={o.key} type="button" data-testid={`calm-${o.key}`} onClick={() => toggleCalm(o.key)}
                      className={`text-sm rounded-lg border px-3 py-1.5 transition ${on ? "bg-[#81B29A] border-[#81B29A] text-white" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                      {on && <Check size={12} className="inline mr-1" />}{o.label}
                    </button>
                  );
                })}
              </div>
              <input data-testid="selfeval-calm-other" value={data.calm_other} onChange={(e) => set("calm_other", e.target.value)} placeholder="Other…"
                className="mt-2 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#7FB069] focus:bg-white" />
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              {FAVOURITE_FIELDS.map((f) => (
                <label key={f.key} className="block">
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-400">{f.label}</span>
                  <input data-testid={`fav-${f.key}`} value={data.favourites[f.key] || ""}
                    onChange={(e) => set("favourites", { ...data.favourites, [f.key]: e.target.value })}
                    className="mt-1 w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#7FB069] focus:bg-white" />
                </label>
              ))}
            </div>

            <TextField label="Anything you'd like your teacher to know about you?" value={data.anything_else} onChange={(v) => set("anything_else", v)} testId="selfeval-anything" rows={2} />
          </section>

          {/* My Goal */}
          <section className="mt-8" data-testid="selfeval-goal-section">
            <h2 className="font-['Outfit'] text-xl font-bold border-b-2 pb-2" style={{ color: T.slate, borderColor: SUBJECT_META.social_emotional.tint }}>🎯 My Goal</h2>
            <TextField label="One thing I want to work on this term is…" value={data.goal} onChange={(v) => set("goal", v)} testId="selfeval-goal" />
            <TextField label="How will I know I'm improving?" value={data.goal_measure} onChange={(v) => set("goal_measure", v)} testId="selfeval-goal-measure" />
          </section>

          {/* AI / Teacher summary */}
          <section className="mt-8 rounded-xl p-5" style={{ backgroundColor: T.grey }} data-testid="selfeval-summary-block">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Teacher summary &amp; comments</p>
            </div>
            <textarea data-testid="selfeval-summary-input" value={data.summary} onChange={(e) => set("summary", e.target.value)}
              rows={Math.max(4, (data.summary || "").split("\n").length + 1)} placeholder="Write your comments, or click 'Summarise for me' to draft from the pupil's responses…"
              className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-slate-700 leading-relaxed outline-none focus:border-[#7FB069] resize-y" />
          </section>

          <div className="mt-9 pt-6 grid sm:grid-cols-2 gap-x-8 gap-y-8" style={{ borderTop: `2px solid ${T.grey}` }}>
            <SignLine label="Student Signature" />
            <SignLine label="Teacher Signature" />
          </div>

          <p className="text-xs text-slate-400 mt-9 pt-5 text-center" style={{ borderTop: `1px solid ${T.grey}` }}>
            BloomTrack · My Social &amp; Emotional Profile · A pupil self-reflection
          </p>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{label}</p>
      <p className="font-semibold text-slate-800 font-['Outfit']">{value || "—"}</p>
    </div>
  );
}

function SignLine({ label }) {
  return (
    <div>
      <div className="h-8 border-b border-slate-400" />
      <p className="text-xs text-slate-500 mt-1.5">{label}</p>
    </div>
  );
}
